<script lang="ts">
  import Button from '$lib/components/ui/button/button.svelte';
  import * as Dialog from '$lib/components/ui/dialog';
  import Input from '$lib/components/ui/input/input.svelte';
  import Label from '$lib/components/ui/label/label.svelte';
  import { toast } from 'svelte-sonner';

  let {
    open = $bindable(true),
    title = $bindable('Import Design'),
    description = $bindable(
      'If you have any unsaved changes, they will be lost when importing a new design.',
    ),
    size = $bindable('sm:max-w-[780px]'),
    trigger,
  }: {
    open?: boolean;
    title?: string;
    description?: string;
    size?: string;
    trigger?: any;
  } = $props();

  let file = $state<File | null>(null);
  let fileInputRef: HTMLInputElement | null = $state(null);

  function handleFileChange(event: Event) {
    const target = event.currentTarget as HTMLInputElement;
    const selectedFile = target.files?.[0] ?? null;
    
    if (selectedFile) {
      // Validate file extension
      const fileExtension = selectedFile.name.split(".").pop()?.toLowerCase();
      if (fileExtension !== "xmd") {
        toast.error('Please select a .xmd file.');
        // Reset the input
        target.value = '';
        file = null;
        return;
      }
      file = selectedFile;
    } else {
      file = null;
    }
  }

  function triggerFileInput() {
    fileInputRef?.click();
  }

  function importNewDesign() {
    if (!file) {
      toast.error('Please select a design file to import.');
      return;
    }

    // Validate file extension again before upload
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (fileExtension !== "xmd") {
      toast.error('Please select a .xmd file.');
      return;
    }

    if (trigger && typeof trigger === 'function') {
      // Emit the uploadDesign event with the file directly
      // The trigger function will extract the file from the source
      const eventObj = {
        name: 'uploadDesign',
        args: [{ from: 'local', path: 'file' }],
      };

      const source = {
        local: {
          file,
        },
      };

      trigger(eventObj, source);
      toast.success(`Design "${file.name}" imported successfully!`);
      open = false;
      file = null;
    } else {
      toast.error('Unable to import design. Please try again.');
    }
  }
</script>

<Dialog.Root bind:open>
  <Dialog.Content class={`${size} border-none shadow-lg`}>
    <Dialog.Header class="!p-4 !pb-0 !mt-4">
      <Dialog.Title class="text-xl font-semibold">{title}</Dialog.Title>
    </Dialog.Header>

    <div
      class="flex !flex-col gap-4 w-full justify-start items-start !p-5 !pt-0"
    >
      <p class="text-sm w-full text-left">{description}</p>

      <div class="flex !flex-col gap-2 w-full">
        <div class="grid w-full items-center gap-1.5">
          <!-- Hidden file input -->
          <Input
            bind:ref={fileInputRef}
            id="file-input"
            type="file"
            accept=".xmd"
            class="hidden"
            onchange={handleFileChange}
          />
          
          <!-- Custom button to trigger file input -->
          <Button
            type="button"
            onclick={triggerFileInput}
            class="h-9 !px-4 !py-6 border-none !shadow-none !text-sm font-normal hover:brightness-110 transition text-white w-full !bg-[#004976] dark:!bg-[var(--theme-link)]"
          >
            {file ? file.name : 'Choose File'}
          </Button>
          
          {#if file}
            <p class="text-sm text-gray-600">Selected: {file.name}</p>
          {/if}
        </div>

        <Button
          type="submit"
          onclick={importNewDesign}
          disabled={!file}
          class="h-9 !px-4 !py-6 border-none !shadow-none !text-sm font-normal hover:brightness-110 transition text-white w-full !bg-[#004976] dark:!bg-[var(--theme-link)]"
        >
          Upload Design
        </Button>
      </div>
    </div>
  </Dialog.Content>
</Dialog.Root>
